/**
 * Token service singleton for managing access token retrieval.
 *
 * This service allows the API client (created outside Auth0 context) to get
 * fresh access tokens by calling getAccessTokenSilently from the Auth0 SDK.
 *
 * The token getter is set by ProtectedRoute once Auth0 context is available.
 * Auth0 SDK handles caching internally - it returns cached tokens instantly
 * and only refreshes when the token is expired.
 */

type TTokenGetter = () => Promise<string | null>;
type TAuthErrorHandler = () => void;
type TInactiveAccountHandler = (detail: { message: string }) => void;

class TokenService {
    private tokenGetter: TTokenGetter | null = null;
    private authErrorHandler: TAuthErrorHandler | null = null;
    private inactiveAccountHandler: TInactiveAccountHandler | null = null;

    /**
     * Set the token getter function.
     * Called from ProtectedRoute once Auth0 context is available.
     */
    setTokenGetter(getter: TTokenGetter): void {
        this.tokenGetter = getter;
    }

    /**
     * Clear the token getter (on logout).
     */
    clearTokenGetter(): void {
        this.tokenGetter = null;
    }

    /**
     * Set the auth error handler.
     * Called when a 401 is received or token refresh fails.
     */
    setAuthErrorHandler(handler: TAuthErrorHandler): void {
        this.authErrorHandler = handler;
    }

    /**
     * Clear the auth error handler.
     */
    clearAuthErrorHandler(): void {
        this.authErrorHandler = null;
    }

    /**
     * Notify that an authentication error occurred.
     * This triggers login redirect.
     */
    notifyAuthError(): void {
        if (this.authErrorHandler) {
            this.authErrorHandler();
        }
    }

    /**
     * Set the handler invoked when the backend rejects a request because the
     * account has been deactivated (403 USER_INACTIVE). Registered by the root
     * layout so a mid-session deactivation shows the full-page notice.
     */
    setInactiveAccountHandler(handler: TInactiveAccountHandler): void {
        this.inactiveAccountHandler = handler;
    }

    /**
     * Clear the inactive-account handler.
     */
    clearInactiveAccountHandler(): void {
        this.inactiveAccountHandler = null;
    }

    /**
     * Notify that the account has been deactivated.
     */
    notifyInactiveAccount(detail: { message: string }): void {
        if (this.inactiveAccountHandler) {
            this.inactiveAccountHandler(detail);
        }
    }

    /**
     * Get a fresh access token.
     * Auth0 SDK caches tokens and only refreshes when expired.
     */
    async getAccessToken(): Promise<string | null> {
        if (!this.tokenGetter) {
            return null;
        }
        return this.tokenGetter();
    }
}

export const tokenService = new TokenService();
